import React from "react";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import AdminDateInput from "../AdminDateInput";
import { formatInr } from "../../utils/financeFormat";
import { RichLineChart } from "../RichChartWidgets";
import AnalyticsLoungeDashboard from "./AnalyticsLoungeDashboard";

function periodKeyShortLabel(pk) {
  if (!pk || !/^\d{4}-\d{2}$/.test(pk)) return pk || "—";
  const [y, m] = pk.split("-").map(Number);
  try {
    return new Intl.DateTimeFormat("en-IN", { month: "short", year: "2-digit" }).format(new Date(y, m - 1, 1));
  } catch {
    return pk;
  }
}

function sumCreditTotals(m) {
  return (Number(m?.bankCreditIciciRupee) || 0) + (Number(m?.bankCreditIdfcRupee) || 0);
}

function sumRentMaint(m) {
  return (Number(m?.rent1Rupee) || 0) + (Number(m?.rent2Rupee) || 0) + (Number(m?.maintenanceRupee) || 0);
}

function bankPlusCash(bankRupee, cashRupee) {
  return (Number(bankRupee) || 0) + (Number(cashRupee) || 0);
}

const METRIC_ROWS = [
  { label: "Electricity", fn: (m) => m.electricityRupee },
  { label: "Gas (HP Gas)", fn: (m) => m.gasRupee },
  { label: "Coal", fn: (m) => bankPlusCash(m.coalRupee, m.coalCashRupee) },
  { label: "Dairy products", fn: (m) => bankPlusCash(m.dairyRupee, m.dairyCashRupee) },
  { label: "Ice Cream", fn: (m) => bankPlusCash(m.iceCreamRupee, m.iceCreamCashRupee) },
  { label: "Chicken (bank)", fn: (m) => m.chickenRupee },
  { label: "Mutton (cash)", fn: (m) => m.muttonCashRupee },
  { label: "Sea Food (cash)", fn: (m) => m.seafoodCashRupee },
  { label: "Meat total", fn: (m) => m.meatTotalRupee },
  { label: "Monthly cash (all)", fn: (m) => m.monthlyCashTotalRupee },
  { label: "Hyper supplier", fn: (m) => m.hyperRupee },
  { label: "Disposables", fn: (m) => m.plasticSpendRupee },
  { label: "Daily expense (manual)", fn: (m) => m.dailyManualExpenseRupee },
  { label: "EasyDiner dine-out", fn: (m) => m.easydinerCreditRupee },
  { label: "Swiggy dine-out", fn: (m) => m.swiggyBundlCreditRupee },
  { label: "Zomato dine-out", fn: (m) => m.zomatoEternalCreditRupee },
  { label: "SBI banquet payment", fn: (m) => m.sbiBanquetCreditRupee },
  { label: "Digital media", fn: (m) => m.digitalMediaRupee },
  { label: "Salaries (Excel)", fn: (m) => m.salaryUploadRupee },
  { label: "Vegetables (bank)", fn: (m) => m.vegetableTotalRupee },
  { label: "Bank credits in", fn: sumCreditTotals },
  { label: "Bank debits out", fn: (m) => m.bankDebitTotalRupee },
  { label: "Rent + maintenance", fn: sumRentMaint },
];

export default function BusinessTrendPanel({
  endPeriodKey,
  onEndPeriodKeyChange,
  comparisonMonths,
  onComparisonMonthsChange,
  monthlyComparison,
  comparisonBusy,
  onRefresh,
}) {
  const months = monthlyComparison?.months || [];
  const trailing = monthlyComparison?.trailingMonths || comparisonMonths;
  const hasChart = months.length > 0;

  const charts = hasChart ? (
    <RichLineChart
      title="Bank flow trends"
      subtitle={`Trailing ${trailing} months ending ${endPeriodKey || "—"}`}
      badge="Compare"
      height={200}
      labels={months.map((m) => m.displayLabel || m.periodKey)}
      series={[
        {
          key: "revenue",
          values: months.map((m) => Number(m.bankDebitTotalRupee) || 0),
        },
        {
          key: "bookings",
          values: months.map(
            (m) => (Number(m.bankCreditIciciRupee) || 0) + (Number(m.bankCreditIdfcRupee) || 0)
          ),
        },
      ]}
    />
  ) : null;

  const toolbar = (
    <>
      <div>
        <Form.Label className="text-secondary small mb-1">End month</Form.Label>
        <AdminDateInput
          inputType="month"
          size="sm"
          style={{ width: 160 }}
          value={endPeriodKey}
          onChange={(e) => onEndPeriodKeyChange(e.target.value)}
        />
      </div>
      <div>
        <Form.Label className="text-secondary small mb-1">Trailing months</Form.Label>
        <Form.Select
          size="sm"
          className="text-bg-dark border-secondary"
          style={{ width: 100 }}
          value={comparisonMonths}
          onChange={(e) => onComparisonMonthsChange(Number(e.target.value))}
        >
          {[3, 4, 6].map((n) => (
            <option key={n} value={n}>
              {n} mo
            </option>
          ))}
        </Form.Select>
      </div>
      <Button size="sm" variant="outline-light" disabled={comparisonBusy} type="button" onClick={onRefresh}>
        Refresh trend
      </Button>
    </>
  );

  return (
    <AnalyticsLoungeDashboard
      title="Business trend"
      subtitle="Multi-month comparison of costs and bank flows — pick end month and trailing window."
      toolbar={toolbar}
      pillItems={[]}
      activePill=""
      onPillSelect={() => {}}
      showCharts={hasChart}
      charts={charts}
    >
      {comparisonBusy ? <Spinner size="sm" animation="border" className="text-light mb-2" /> : null}
      {months.length >= 2 ? (
        <div className="finance-recon-scroll">
          <h4 className="h6 text-info mb-2">Business trend — last {trailing} months</h4>
          <Table
            striped
            bordered
            hover
            size="sm"
            variant="dark"
            responsive
            className="mb-0 finance-comparison-table admin-console-data-table"
          >
            <thead>
              <tr>
                <th>Metric</th>
                {months.map((m) => (
                  <th key={`cmp-h-${m.periodKey}`} className="text-end">
                    {periodKeyShortLabel(m.periodKey)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METRIC_ROWS.map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  {months.map((m) => (
                    <td key={`cmp-${row.label}-${m.periodKey}`} className="text-end">
                      {formatInr(row.fn(m))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <p className="text-secondary small mb-0">
          Choose an end month and trailing window, then refresh. Upload bank data in Monthly costs / Uploads if rows are empty.
        </p>
      )}
    </AnalyticsLoungeDashboard>
  );
}
